import Button from '@atoms/Button'
import Icon from '@components/utils/Icon'

import { scopedTranslation } from '@utils/I18n'

import { isFilterActive, type CoverageFilter, type RosterFilters, type StatusFilter, type SupporterOption } from './types'

const t = scopedTranslation('roster_board')

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <div className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-primary-50 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:text-primary-700">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('filters.chips.remove', { label })}
        className="tw:flex tw:min-h-6 tw:min-w-6 tw:items-center tw:justify-center tw:rounded tw:text-primary-500 tw:hover:text-primary-800 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-400"
      >
        <Icon type="x-close" width={12} height={12} aria-hidden />
      </button>
    </div>
  )
}

type ActiveFiltersBarProps = {
  filters: RosterFilters
  peopleOptions: SupporterOption[]
  onRemoveRole: (role: string) => void
  onRemovePerson: (id: string) => void
  onStatus: (status: StatusFilter) => void
  onCoverage: (coverage: CoverageFilter) => void
  onClear: () => void
}

export function ActiveFiltersBar({
  filters,
  peopleOptions,
  onRemoveRole,
  onRemovePerson,
  onStatus,
  onCoverage,
  onClear,
}: ActiveFiltersBarProps) {
  if (!isFilterActive(filters)) return null

  const personName = (id: string) => peopleOptions.find((option) => option.id === id)?.fullName ?? id

  return (
    <div role="group" aria-label={t('filters.active_label')} className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
      {filters.roles.map((role) => (
        <FilterChip key={`role-${role}`} label={t('filters.chips.role', { value: role })} onRemove={() => onRemoveRole(role)} />
      ))}
      {filters.people.map((id) => (
        <FilterChip key={`person-${id}`} label={personName(id)} onRemove={() => onRemovePerson(id)} />
      ))}
      {filters.status !== 'all' && (
        <FilterChip label={t(`filters.chips.status_${filters.status}`)} onRemove={() => onStatus('all')} />
      )}
      {filters.coverage !== 'any' && (
        <FilterChip label={t(`filters.chips.coverage_${filters.coverage}`)} onRemove={() => onCoverage('any')} />
      )}
      <Button rank="link" size="sm" leadingIcon="refresh" onClick={onClear}>
        {t('filters.chips.clear')}
      </Button>
    </div>
  )
}
